import { useState } from "react";
import { Undo2 } from "lucide-react";
import type {
  BuyerSmartImportBatchDto,
  BuyerSmartImportUndoResultDto,
} from "@/api/buyerSmartImport";
import {
  Button,
  StatusBadge,
  SubTable,
  Timestamp,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { formatNumber } from "@/design-system/lib/format";
import { smartImportCopy } from "./smartImportCopy";
import { batchStatusTone } from "./smartImportStatus";

/**
 * Past imports, and the undo (D13).
 *
 * <p>🔴 <b>Undo is offered from the SERVER's `canUndo`, never from the status
 * alone.</b> The rule is "the importer, or a `Buyers.ManageAllTeams` holder",
 * and it is resolved per row server-side — a client that re-derived it from
 * `status === "Completed"` would offer the action to somebody the endpoint
 * refuses, which is a button that exists to fail.</p>
 */

export interface SmartImportHistoryProps {
  batches: readonly BuyerSmartImportBatchDto[];
  loading?: boolean;
  onUndo: (batchId: string) => void;
  undoing?: boolean;
  /** The batch currently being undone, so only its row shows the pending state. */
  undoingBatchId?: string | null;
  /** The last undo's result, rendered beneath the table. */
  undoResult?: BuyerSmartImportUndoResultDto | null;
  undoError?: string | null;
}

export function SmartImportHistory({
  batches,
  loading,
  onUndo,
  undoing,
  undoingBatchId,
  undoResult,
  undoError,
}: SmartImportHistoryProps) {
  const [confirming, setConfirming] = useState<BuyerSmartImportBatchDto | null>(null);

  const columns: SubTableColumn<BuyerSmartImportBatchDto>[] = [
    {
      key: "file",
      header: "File",
      render: (b) => <span className="font-medium text-onyx break-all">{b.fileName}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (b) => (
        <div className="flex flex-col items-start gap-space-1">
          <StatusBadge tone={batchStatusTone(b.status)} data-testid={`batch-status-${b.id}`}>
            {b.status === "Undone" ? smartImportCopy.undoneLabel : b.status}
          </StatusBadge>
          {b.status === "Failed" && b.failureReason && (
            <span className="text-caption text-status-error">{b.failureReason}</span>
          )}
          {b.status === "Undone" && b.undoneAtUtc && (
            <span className="text-caption text-gray-mid">
              <Timestamp value={b.undoneAtUtc} />
              {b.undoArchivedCount != null && (
                <> · {formatNumber(b.undoArchivedCount)} {smartImportCopy.undoArchived.toLowerCase()}</>
              )}
              {b.undoKeptCount != null && b.undoKeptCount > 0 && (
                <> · {formatNumber(b.undoKeptCount)} {smartImportCopy.undoKept.toLowerCase()}</>
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "who",
      header: "Run by",
      render: (b) => (
        <div className="flex flex-col gap-space-1">
          <span className="text-onyx">{b.runByDisplayName ?? b.runByUserId}</span>
          <span className="text-caption text-gray-mid">
            <Timestamp value={b.createdAt} />
          </span>
        </div>
      ),
    },
    {
      key: "counts",
      header: "Counts",
      render: (b) => (
        <span className="text-caption text-gray-mid" data-testid={`batch-counts-${b.id}`}>
          {formatNumber(b.counts.rowsRead)} read · {formatNumber(b.counts.created)} created ·{" "}
          {formatNumber(b.counts.merged)} merged ·{" "}
          {formatNumber(b.counts.alreadyImported)} already imported ·{" "}
          {formatNumber(b.counts.beforeCutoff)} before the cutoff ·{" "}
          {formatNumber(b.counts.rejected)} rejected
        </span>
      ),
    },
    {
      key: "undo",
      header: "",
      align: "right",
      render: (b) =>
        b.canUndo ? (
          <Button
            variant="text"
            size="sm"
            data-testid={`batch-undo-${b.id}`}
            disabled={undoing}
            onClick={() => setConfirming(b)}
          >
            <Undo2 size={14} strokeWidth={1.5} aria-hidden />
            {undoing && undoingBatchId === b.id
              ? smartImportCopy.undoRunning
              : smartImportCopy.undoAction}
          </Button>
        ) : null,
    },
  ];

  return (
    <section aria-label={smartImportCopy.historyTitle} data-testid="smart-import-history">
      <h2 className="font-display font-semibold text-lead text-onyx mb-space-2">
        {smartImportCopy.historyTitle}
      </h2>

      <SubTable
        caption={smartImportCopy.historyTitle}
        data-testid="smart-import-history-table"
        columns={columns}
        rows={[...batches]}
        rowKey={(b) => b.id}
        rowTestId={(b) => `batch-row-${b.id}`}
        empty={{ message: loading ? "Loading…" : smartImportCopy.historyEmpty }}
      />

      {undoError && (
        <p
          role="alert"
          className="mt-space-2 text-small text-status-error"
          data-testid="smart-import-undo-error"
        >
          {undoError}
        </p>
      )}

      {undoResult && <UndoResult result={undoResult} />}

      {confirming && (
        <ConfirmModal
          open
          title={smartImportCopy.undoConfirmTitle}
          message={
            <span>
              {formatNumber(confirming.counts.created)} buyers were created by{" "}
              <strong>{confirming.fileName}</strong>. Undoing archives the ones nobody has
              worked since, and leaves the rest alone — you will be told which.
            </span>
          }
          confirmLabel={smartImportCopy.undoConfirmAction}
          destructive
          onConfirm={() => {
            const id = confirming.id;
            setConfirming(null);
            onUndo(id);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}
    </section>
  );
}

/**
 * What the undo kept, and why (D13).
 *
 * <p>🔴 <b>The reasons are the point.</b> An undo that archived 40 of 50 and
 * said nothing would leave an operator to work out which ten are still on the
 * book and why — and the answer ("somebody logged a contact", "it converted")
 * is precisely the thing that makes leaving them alone correct rather than a
 * failure.</p>
 */
function UndoResult({ result }: { result: BuyerSmartImportUndoResultDto }) {
  const columns: SubTableColumn<BuyerSmartImportUndoResultDto["kept"][number]>[] = [
    { key: "name", header: "Buyer", render: (k) => <span className="text-onyx">{k.name}</span> },
    {
      key: "reason",
      header: "Why it was kept",
      render: (k) => <span className="text-gray-mid">{k.reason}</span>,
    },
  ];

  return (
    <div className="mt-space-3 grid gap-space-2" data-testid="smart-import-undo-result">
      <p className="text-small text-onyx" data-testid="smart-import-undo-archived">
        {formatNumber(result.archivedCount)} {smartImportCopy.undoArchived.toLowerCase()} ·{" "}
        {formatNumber(result.kept.length)} {smartImportCopy.undoKept.toLowerCase()}
      </p>
      {result.kept.length > 0 && (
        <>
          <p className="text-caption text-gray-mid">{smartImportCopy.undoKeptHint}</p>
          <SubTable
            caption={smartImportCopy.undoKeptTitle}
            data-testid="smart-import-undo-kept-table"
            columns={columns}
            rows={[...result.kept]}
            rowKey={(k) => k.buyerLeadId}
            rowTestId={(k) => `undo-kept-${k.buyerLeadId}`}
            empty={{ message: "Every buyer this import created was archived." }}
          />
        </>
      )}
    </div>
  );
}
